/**
 * API Service for communicating with the backend.
 */

import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim() !== '')
  ? process.env.REACT_APP_API_URL.trim().replace(/\/$/, '')
  : '';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes
  headers: {
    'Content-Type': 'application/json',
  },
  // Ensure large responses are handled properly
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  // Force JSON response type
  responseType: 'json',
  // Transform response to ensure proper JSON parsing
  transformResponse: [function (data) {
    // If data is already an object, return it
    if (typeof data === 'object' && data !== null) {
      return data;
    }
    // If data is a string, try to parse it as JSON
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error('Failed to parse response as JSON:', e);
        return data;
      }
    }
    return data;
  }],
});

// No API key required - API is open

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error - but still return the data structure if available
      console.error('API Error:', error.response.data);

      // If the response has a proper error structure with success: false, return it
      // This allows the calling code to handle the structured error response
      if (error.response.data && typeof error.response.data === 'object') {
        // Return the error response data so components can access full structure
        return Promise.reject({
          isApiError: true,
          data: error.response.data,
          status: error.response.status,
          message: error.response.data.error || 'An error occurred'
        });
      }

      throw new Error(error.response.data.error || 'An error occurred');
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.request);
      throw new Error('Network error. Please check your connection.');
    } else {
      // Something else happened
      console.error('Error:', error.message);
      throw new Error(error.message);
    }
  }
);

// SAP Operations
export const sapApi = {
  /**
   * Get list of available entities
   */
  getEntities: async () => {
    const response = await apiClient.get('/api/v1/sap/entities');
    return response.data;
  },

  /**
   * Pull Trial Balance from SAP
   */
  pullTrialBalance: async (
    entityName,
    startDate,
    endDate,
    storeInS3 = true,
    options = {}
  ) => {
    const payload = {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
      store_in_s3: storeInS3,
    };

    if (options.xeroRedirectUrl) {
      payload.xero_redirect_url = options.xeroRedirectUrl;
    }
    if (options.exactRedirectUrl) {
      payload.exact_redirect_url = options.exactRedirectUrl;
    }

    const response = await apiClient.post('/api/v1/sap/pull-tb', payload);
    return response.data;
  },

  /**
   * Get TB status for an entity
   * @param {string} entityName - Entity name
   * @param {string} [startDate] - Start date filter (YYYY-MM-DD format, optional)
   * @param {string} [endDate] - End date filter (YYYY-MM-DD format, optional)
   */
  getTBStatus: async (entityName, startDate = null, endDate = null) => {
    const params = { entity_name: entityName };
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }
    const response = await apiClient.get('/api/v1/sap/tb-status', { params });
    return response.data;
  },

  /**
   * Test SAP connection
   */
  testConnection: async (entityName) => {
    const response = await apiClient.post('/api/v1/sap/test-connection', {
      entity_name: entityName,
    });
    return response.data;
  },

  /**
   * Get TB data (actual data, not just status)
   * @param {string} entityName - Entity name
   * @param {string} startDate - Start date (YYYY-MM-DD format, required)
   * @param {string} endDate - End date (YYYY-MM-DD format, required)
   * @param {string} [dataPoint] - Data point type (default: 'tb')
   */
  getTBData: async (entityName, startDate, endDate, dataPoint = 'tb') => {
    const params = {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
      data_point: dataPoint,
    };
    const response = await apiClient.get('/api/v1/sap/tb-data', { params });
    return response.data;
  },

  /**
   * Execute custom SQL query on SAP
   */
  executeQuery: async (entityName, query) => {
    try {
      const response = await apiClient.post('/api/v1/sap/execute-query', {
        entity_name: entityName,
        query: query,
      });

      // Log the response structure for debugging
      console.log('API Response received:', {
        status: response.status,
        hasData: !!response.data,
        dataType: typeof response.data,
        dataKeys: response.data ? Object.keys(response.data) : 'none'
      });

      // Additional validation logging
      if (response.data) {
        console.log('Response validation:', {
          hasSuccess: 'success' in response.data,
          successValue: response.data.success,
          hasColumns: 'columns' in response.data,
          hasData: 'data' in response.data,
          columnsType: response.data.columns ? typeof response.data.columns : 'missing',
          dataType: response.data.data ? typeof response.data.data : 'missing',
          columnsIsArray: Array.isArray(response.data.columns),
          dataIsArray: Array.isArray(response.data.data),
          rowCount: response.data.row_count,
          columnCount: response.data.column_count
        });

        // Log actual data to console for debugging
        if (response.data.data && response.data.columns) {
          console.table(response.data.data.slice(0, 5)); // Show first 5 rows in console
        }
      }

      return response.data;
    } catch (error) {
      // Re-throw with additional context
      console.error('Query API call failed:', error);
      throw error;
    }
  },

  /**
   * Execute custom SQL query and download as CSV
   */
  executeQueryCsv: async (entityName, query) => {
    // Override the default transformResponse for CSV downloads
    const response = await apiClient.post('/api/v1/sap/execute-query-csv', {
      entity_name: entityName,
      query: query,
    }, {
      responseType: 'blob',
      transformResponse: [], // Don't transform blob responses
    });
    return response.data;
  },

  /**
   * Download Trial Balance template Excel file for manual upload
   */
  downloadTBTemplate: async () => {
    const response = await apiClient.get('/api/v1/sap/download-tb-template', {
      responseType: 'blob',
      transformResponse: [], // Don't transform blob responses
    });
    return response.data;
  },

  /**
   * Upload Trial Balance Excel/CSV for entities with no system access (connection_type: "N/A")
   */
  uploadTrialBalance: async (entityName, startDate, endDate, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('start_date', startDate);
    formData.append('end_date', endDate);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/sap/upload-tb', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// RPT Operations
export const rptApi = {
  /**
   * Fetch General Ledger from SAP and store on S3
   */
  fetchGL: async (entityName, startDate, endDate) => {
    const response = await apiClient.post('/api/v1/rpt/fetch-gl', {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
    });
    return response.data;
  },

  /**
   * Upload General Ledger file for entities with no system access (connection_type: "N/A")
   */
  uploadGL: async (entityName, startDate, endDate, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('start_date', startDate);
    formData.append('end_date', endDate);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/rpt/upload-gl', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download General Ledger template for manual upload
   */
  downloadGLTemplate: async () => {
    const response = await apiClient.get('/api/v1/rpt/gl-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download a previously uploaded / fetched GL CSV from S3.
   *
   * @param {string} entityName
   * @param {string} period   e.g. "2025-04-01_to_2026-03-31"
   * @param {string} [timestamp]  Optional specific upload timestamp; if absent, returns latest.
   */
  downloadGL: async (entityName, period, timestamp = null) => {
    const params = { entity_name: entityName, period };
    if (timestamp) params.timestamp = timestamp;
    const response = await apiClient.get('/api/v1/rpt/download-gl', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * List all GL versions for an entity
   */
  listGL: async (entityName, period = null) => {
    const params = { entity_name: entityName };
    if (period) {
      params.period = period;
    }
    const response = await apiClient.get('/api/v1/rpt/gl-list', { params });
    return response.data;
  },

  /**
   * Generate RPT from stored GL data
   */
  generateRPT: async (entityName, startDate, endDate, useAiDetection = false) => {
    const requestBody = {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
      use_ai_detection: useAiDetection,
    };
    const response = await apiClient.post('/api/v1/rpt/generate', requestBody);
    return response.data;
  },

  /**
   * Get RPT status for an entity
   */
  getRPTStatus: async (entityName, period = null) => {
    const params = { entity_name: entityName };
    if (period) {
      params.period = period;
    }
    const response = await apiClient.get('/api/v1/rpt/status', { params });
    return response.data;
  },

  /**
   * View RPT details (item-by-item)
   */
  viewRPT: async (entityName, timestamp = null, period = null, page = 1, pageSize = 50, sheet = 'filtered') => {
    const params = {
      entity_name: entityName,
      page: page,
      page_size: pageSize,
      sheet: sheet,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    if (period) {
      params.period = period;
    }
    const response = await apiClient.get('/api/v1/rpt/view', { params });
    return response.data;
  },

  /**
   * Approve RPT with user modifications
   */
  approveRPT: async (entityName, timestamp, period, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('timestamp', timestamp);
    formData.append('period', period);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/rpt/approve', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download RPT Excel file
   */
  downloadRPT: async (entityName, timestamp = null, period = null) => {
    const params = { entity_name: entityName };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    if (period) {
      params.period = period;
    }
    const response = await apiClient.get('/api/v1/rpt/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// RPT V2 Operations (Schedule III / IndAS Compliant - EL_SAP_B1 entities only)
export const rptV2Api = {
  /**
   * Check if entity is eligible for RPT V2 automation
   * @param {string} entityName - Entity name
   * @returns {Promise<Object>} - Eligibility status
   */
  checkEligibility: async (entityName) => {
    const response = await apiClient.get('/api/v1/rpt-v2/check-eligibility', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Get list of RPT V2 nature categories (Schedule III / IndAS compliant)
   * @param {boolean} grouped - If true, returns natures organized by category
   * @returns {Promise<Object>} - RPT natures
   */
  getNatures: async (grouped = false) => {
    const response = await apiClient.get('/api/v1/rpt-v2/natures', {
      params: { grouped: grouped ? 'true' : 'false' },
    });
    return response.data;
  },

  /**
   * Generate RPT V2 report from stored GL data
   * @param {string} entityName - Entity name
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {Object} options - Optional parameters
   * @param {boolean} options.useLLMClassification - Use LLM for nature classification (~$3-4/entity)
   * @param {string} options.parentCompany - Parent company name for LLM context
   * @param {string} options.industry - Industry for LLM context
   * @param {number} options.llmMaxConcurrentBatches - Max LLM batches in parallel (1-10; 3-5 recommended for speed)
   * @returns {Promise<Object>} - Generation result
   */
  generateRPT: async (entityName, startDate, endDate, options = {}) => {
    const response = await apiClient.post('/api/v1/rpt-v2/generate', {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
      use_llm_classification: options.useLLMClassification || false,
      parent_company: options.parentCompany || null,
      industry: options.industry || 'Healthcare / Life Sciences',
      llm_max_concurrent_batches: options.llmMaxConcurrentBatches != null ? options.llmMaxConcurrentBatches : 1,
    });
    return response.data;
  },

  /**
   * Download RPT V2 Excel file
   * @param {string} entityName - Entity name
   * @param {string} timestamp - Generation timestamp
   * @param {string} period - Period in YYYY-MM format
   * @returns {Promise<Blob>} - Excel file blob
   */
  downloadRPT: async (entityName, timestamp, period) => {
    const response = await apiClient.get('/api/v1/rpt-v2/download', {
      params: {
        entity_name: entityName,
        timestamp: timestamp,
        period: period,
      },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * List all RPT V2 reports for an entity
   * @param {string} entityName - Entity name
   * @param {string} period - Optional period filter (YYYY-MM format)
   * @returns {Promise<Object>} - List of reports
   */
  listReports: async (entityName, period = null) => {
    const params = { entity_name: entityName };
    if (period) {
      params.period = period;
    }
    const response = await apiClient.get('/api/v1/rpt-v2/list', { params });
    return response.data;
  },
};

// RPT V3 Operations (Rule-based CPM RPT Analyzer - EL_RPT entities)
export const rptV3Api = {
  /**
   * Check if entity is eligible for RPT V3 automation
   * @param {string} entityName - Entity name
   * @returns {Promise<Object>} - Eligibility status
   */
  checkEligibility: async (entityName) => {
    const response = await apiClient.get('/api/v1/rpt-v3/check-eligibility', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Get list of RPT V3 nature categories (rule-based classification)
   * @returns {Promise<Object>} - RPT natures
   */
  getNatures: async () => {
    const response = await apiClient.get('/api/v1/rpt-v3/natures');
    return response.data;
  },

  /**
   * Get list of all eligible entities for RPT V3
   * @returns {Promise<Object>} - List of eligible entities
   */
  getEligibleEntities: async () => {
    const response = await apiClient.get('/api/v1/rpt-v3/eligible-entities');
    return response.data;
  },

  /**
   * Generate RPT V3 report from stored GL data
   * @param {string} entityName - Entity name
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} - Generation result
   */
  generateRPT: async (entityName, startDate, endDate) => {
    const response = await apiClient.post('/api/v1/rpt-v3/generate', {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
    });
    return response.data;
  },

  /**
   * Download RPT V3 Excel file
   * @param {string} entityName - Entity name
   * @param {string} timestamp - Generation timestamp
   * @param {string} period - Period string (e.g., "2025-04-01_to_2025-12-31")
   * @returns {Promise<Blob>} - Excel file blob
   */
  downloadRPT: async (entityName, timestamp, period) => {
    const response = await apiClient.get('/api/v1/rpt-v3/download', {
      params: {
        entity_name: entityName,
        timestamp: timestamp,
        period: period,
      },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * List all RPT V3 reports for an entity
   * @param {string} entityName - Entity name
   * @param {string} period - Optional period filter
   * @returns {Promise<Object>} - List of reports
   */
  listReports: async (entityName, period = null) => {
    const params = { entity_name: entityName };
    if (period) {
      params.period = period;
    }
    const response = await apiClient.get('/api/v1/rpt-v3/list', { params });
    return response.data;
  },

  /**
   * List user-maintained GL account -> nature mappings for an entity
   */
  listGLNatureMappings: async (entityName, includeBuiltin = false) => {
    const response = await apiClient.get('/api/v1/rpt-v3/config/gl-nature', {
      params: {
        entity_name: entityName,
        include_builtin: includeBuiltin,
      },
    });
    return response.data;
  },

  /**
   * Add a user-maintained GL account -> nature mapping
   */
  addGLNatureMapping: async (entityName, mapping) => {
    const response = await apiClient.post('/api/v1/rpt-v3/config/gl-nature', {
      entity_name: entityName,
      ...mapping,
    });
    return response.data;
  },

  /**
   * Update a user-maintained GL account -> nature mapping
   */
  updateGLNatureMapping: async (mappingId, mapping) => {
    const response = await apiClient.put(`/api/v1/rpt-v3/config/gl-nature/${mappingId}`, mapping);
    return response.data;
  },

  /**
   * Disable a user-maintained GL account -> nature mapping
   */
  disableGLNatureMapping: async (mappingId) => {
    const response = await apiClient.patch(`/api/v1/rpt-v3/config/gl-nature/${mappingId}/disable`);
    return response.data;
  },

  /**
   * Validate pending GL account -> nature mappings without saving
   */
  validateGLNatureMappings: async (entityName, mappings) => {
    const response = await apiClient.post('/api/v1/rpt-v3/config/gl-nature/validate', {
      entity_name: entityName,
      mappings,
    });
    return response.data;
  },
};

// FinAlyzer Operations
export const finalyzerApi = {
  /**
   * Push Trial Balance to FinAlyzer (from S3)
   */
  pushTrialBalance: async (entityName, endDate, startDate = null) => {
    const payload = {
      entity_name: entityName,
      end_date: endDate,
      use_latest: true,
    };
    if (startDate) {
      payload.start_date = startDate;
    }
    const response = await apiClient.post('/api/v1/finalyzer/push-tb', payload);
    return response.data;
  },

  /**
   * Test FinAlyzer connection
   */
  testConnection: async () => {
    const response = await apiClient.post('/api/v1/finalyzer/test-connection');
    return response.data;
  },
};

// Lease (ROU & Lease Liability) Operations
export const leaseApi = {
  /**
   * Download lease input template
   */
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/lease/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload lease input data
   */
  uploadLeaseData: async (entityName, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/lease/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Get lease data status for an entity
   */
  getLeaseStatus: async (entityName) => {
    const response = await apiClient.get('/api/v1/lease/status', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download lease input data
   */
  downloadLeaseData: async (entityName, timestamp = null) => {
    const params = { entity_name: entityName };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/lease/data', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Calculate lease analysis for a period
   */
  calculateLeaseAnalysis: async (entityName, startDate, endDate, inputTimestamp = null, rouManualAdjustment = null) => {
    const payload = {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
    };
    if (inputTimestamp) {
      payload.input_timestamp = inputTimestamp;
    }
    // Optional user-entered ROU manual movements (EL group only). Each is a signed
    // delta added to the computed Finalyzer Input row; only send values actually
    // provided. Keys map to the backend's rou_* fields.
    if (rouManualAdjustment) {
      const fieldMap = {
        gbAddition: 'rou_gb_addition',
        adDepreciation: 'rou_ad_depreciation',
        gbDisposal: 'rou_gb_disposal',
        adDisposal: 'rou_ad_disposal',
        gbAdjustment: 'rou_gb_adjustment',
        adAdjustment: 'rou_ad_adjustment',
      };
      Object.entries(fieldMap).forEach(([key, field]) => {
        const v = rouManualAdjustment[key];
        if (v !== null && v !== undefined) {
          payload[field] = v;
        }
      });
    }
    const response = await apiClient.post('/api/v1/lease/calculate', payload);
    return response.data;
  },

  /**
   * List all lease calculation outputs
   */
  listLeaseOutputs: async (entityName) => {
    const response = await apiClient.get('/api/v1/lease/outputs', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download lease calculation output
   */
  downloadLeaseOutput: async (entityName, startDate, endDate, timestamp = null) => {
    const params = {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/lease/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// PPE (Property, Plant & Equipment) Operations
export const ppeApi = {
  /**
   * Download FAR input template
   */
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/ppe/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload Fixed Asset Register (FAR) data
   */
  uploadFARData: async (entityName, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/ppe/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Get FAR data status for an entity
   */
  getFARStatus: async (entityName) => {
    const response = await apiClient.get('/api/v1/ppe/status', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download FAR input data
   */
  downloadFARData: async (entityName, timestamp = null) => {
    const params = { entity_name: entityName };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/ppe/data', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Calculate PPE report for a period
   */
  calculatePPEReport: async (entityName, startDate, endDate, inputTimestamp = null) => {
    const payload = {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
    };
    if (inputTimestamp) {
      payload.input_timestamp = inputTimestamp;
    }
    const response = await apiClient.post('/api/v1/ppe/calculate', payload);
    return response.data;
  },

  /**
   * List all PPE calculation outputs
   */
  listPPEOutputs: async (entityName) => {
    const response = await apiClient.get('/api/v1/ppe/outputs', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download PPE calculation output
   */
  downloadPPEOutput: async (entityName, period, timestamp = null) => {
    // Parse period string (format: "YYYY-MM-DD_to_YYYY-MM-DD") to extract dates
    const [startDate, endDate] = period.split('_to_');

    const params = {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/ppe/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// FX Rates Operations
export const fxRatesApi = {
  /**
   * Fetch FX rates from all SAP entities
   */
  fetchFXRates: async (startDate, endDate) => {
    const response = await apiClient.post('/api/v1/fx-rates/fetch', {
      start_date: startDate,
      end_date: endDate,
    });
    return response.data;
  },

  /**
   * List all available FX rates files
   */
  listFXRates: async () => {
    const response = await apiClient.get('/api/v1/fx-rates/list');
    return response.data;
  },

  /**
   * Download FX rates Excel file
   */
  downloadFXRates: async (period, timestamp = null) => {
    const params = { period };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/fx-rates/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// ============================================================================
// Retained Earnings API
// ============================================================================

export const retainedEarningsApi = {
  /**
   * Calculate retained earnings adjustment for an entity (Schedule III/IndAS)
   * @param {string} entityName - Entity name
   * @param {string} fiscalYearEnd - Fiscal year end date (YYYY-MM-DD), must be 31 March
   * @returns {Promise<object>} Calculation result
   */
  calculateAdjustment: async (entityName, fiscalYearEnd) => {
    const response = await apiClient.post('/api/v1/retained-earnings/calculate', {
      entity_name: entityName,
      fiscal_year_end: fiscalYearEnd,
    });
    return response.data;
  },

  /**
   * Get retained earnings calculation history for an entity
   * @param {string} entityName - Entity name
   * @param {string} [fiscalYearEnd] - Optional fiscal year end filter (YYYY-MM-DD)
   * @returns {Promise<object>} History data
   */
  getHistory: async (entityName, fiscalYearEnd = null) => {
    const params = { entity_name: entityName };
    if (fiscalYearEnd) {
      params.fiscal_year_end = fiscalYearEnd;
    }
    const response = await apiClient.get('/api/v1/retained-earnings/history', { params });
    return response.data;
  },

  /**
   * Download retained earnings workbook
   * @param {string} entityName - Entity name
   * @param {string} fiscalYearEnd - Fiscal year end date (YYYY-MM-DD)
   * @param {string} timestamp - Timestamp of the calculation
   * @returns {Promise<Blob>} Excel file blob
   */
  downloadWorkbook: async (entityName, fiscalYearEnd, timestamp) => {
    const response = await apiClient.get('/api/v1/retained-earnings/download', {
      params: {
        entity_name: entityName,
        fiscal_year_end: fiscalYearEnd,
        timestamp,
      },
      responseType: 'blob',
      transformResponse: [],
    });
    return response.data;
  },
};

// Fixed Deposit (FD) Operations
export const fdApi = {
  /**
   * Download FD input template
   */
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/fd/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload FD input data
   */
  uploadFDData: async (entityName, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/fd/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download FD input data
   */
  downloadFDData: async (entityName) => {
    const response = await apiClient.get('/api/v1/fd/data', {
      params: { entity_name: entityName },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Calculate FD analysis
   */
  calculateFDAnalysis: async (entityName, reportingDate, includeInterestSchedule = false, includeTBReconciliation = false) => {
    const response = await apiClient.post('/api/v1/fd/calculate', {
      entity_name: entityName,
      reporting_date: reportingDate,
      include_interest_schedule: includeInterestSchedule,
      include_tb_reconciliation: includeTBReconciliation,
    });
    return response.data;
  },

  /**
   * List all FD analysis outputs
   */
  listFDOutputs: async (entityName) => {
    const response = await apiClient.get('/api/v1/fd/outputs', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download FD analysis output
   */
  downloadFDOutput: async (entityName, reportingDate, timestamp = null) => {
    const params = {
      entity_name: entityName,
      reporting_date: reportingDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/fd/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Health check for FD module
   */
  healthCheck: async () => {
    const response = await apiClient.get('/api/v1/fd/health');
    return response.data;
  },

  /**
   * Generate FD schedule V2 (AI-powered PDF extraction)
   * @param {string} entityName - Full entity name from entities.yaml
   * @param {string} fromDate - Period start date (YYYY-MM-DD)
   * @param {string} toDate - Period end date (YYYY-MM-DD)
   * @param {string} referenceDate - Reference date for classification (YYYY-MM-DD, optional)
   * @param {Array<File>} files - Array of PDF files
   */
  generateFDV2: async (entityName, fromDate, toDate, referenceDate, files, interestStatementFile = null) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('from_date', fromDate);
    formData.append('to_date', toDate);
    if (referenceDate) {
      formData.append('reference_date', referenceDate);
    }

    // Append all PDF/TXT/HTML files
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Optional: HDFC interest statement Excel for Interest Received column
    if (interestStatementFile) {
      formData.append('interest_statement', interestStatementFile);
    }

    const response = await apiClient.post('/api/v1/fd/v2/generate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes for AI processing
    });
    return response.data;
  },

  /**
   * List all FD V2 outputs for an entity
   * @param {string} entityName - Full entity name
   */
  listFDV2Outputs: async (entityName) => {
    const response = await apiClient.get('/api/v1/fd/v2/outputs', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download FD V2 output
   * @param {string} entityName - Full entity name
   * @param {string} fromDate - Period start date (YYYY-MM-DD)
   * @param {string} toDate - Period end date (YYYY-MM-DD)
   * @param {string} timestamp - Optional timestamp for specific version
   */
  downloadFDV2Output: async (entityName, fromDate, toDate, timestamp = null) => {
    const params = {
      entity_name: entityName,
      from_date: fromDate,
      to_date: toDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/fd/v2/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// Borrowing & Finance Costs Operations
export const borrowingApi = {
  /**
   * Download borrowing input template
   */
  downloadTemplate: async (entityName = 'default') => {
    const response = await apiClient.get('/api/v1/borrowing/template', {
      params: { entity_name: entityName },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload borrowing input data
   */
  uploadBorrowingData: async (entityName, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/borrowing/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Get borrowing data status for an entity
   */
  getBorrowingStatus: async (entityName) => {
    const response = await apiClient.get('/api/v1/borrowing/status', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download borrowing input data
   */
  downloadBorrowingData: async (entityName) => {
    const response = await apiClient.get('/api/v1/borrowing/data', {
      params: { entity_name: entityName },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Calculate borrowing analysis
   */
  calculateBorrowingAnalysis: async (entityName, reportingDate, includeRepaymentSchedule = true, includeTBReconciliation = false) => {
    const response = await apiClient.post('/api/v1/borrowing/calculate', {
      entity_name: entityName,
      reporting_date: reportingDate,
      include_repayment_schedule: includeRepaymentSchedule,
      include_tb_reconciliation: includeTBReconciliation,
    });
    return response.data;
  },

  /**
   * List all borrowing analysis outputs
   */
  listBorrowingOutputs: async (entityName) => {
    const response = await apiClient.get('/api/v1/borrowing/outputs', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download borrowing analysis output
   */
  downloadBorrowingOutput: async (entityName, reportingDate, timestamp = null) => {
    const params = {
      entity_name: entityName,
      reporting_date: reportingDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/borrowing/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Health check for borrowing module
   */
  healthCheck: async () => {
    const response = await apiClient.get('/api/v1/borrowing/health');
    return response.data;
  },
};

// FCTR (Foreign Currency Translation Reserve) Operations
export const fctrApi = {
  /**
   * Get list of entities applicable for FCTR calculation
   */
  getApplicableEntities: async () => {
    const response = await apiClient.get('/api/v1/fctr/applicable-entities');
    return response.data;
  },

  /**
   * Generate FCTR calculation for an entity
   */
  generateFCTR: async (entityName, startDate, endDate, manualRates = null) => {
    const payload = {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
    };

    // Add manual rates if provided
    if (manualRates) {
      payload.manual_rates = manualRates;
    }

    const response = await apiClient.post('/api/v1/fctr/generate', payload);
    return response.data;
  },

  /**
   * Get FCTR calculation history for an entity
   */
  getHistory: async (entityName, startDate = null, endDate = null) => {
    const params = { entity: entityName };
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }
    const response = await apiClient.get('/api/v1/fctr/history', { params });
    return response.data;
  },

  /**
   * Download FCTR workbook
   */
  downloadWorkbook: async (entityName, startDate, endDate, timestamp) => {
    const response = await apiClient.get('/api/v1/fctr/download', {
      params: {
        entity: entityName,
        start_date: startDate,
        end_date: endDate,
        timestamp: timestamp,
      },
      responseType: 'blob',
    });
    return response.data;
  },
};

// SOCIE (Statement of Changes in Equity) Operations
export const socieApi = {
  /**
   * Download SOCIE movements template
   */
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/socie/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload SOCIE movements data
   */
  uploadMovements: async (entityName, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/socie/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Get SOCIE movements status for an entity
   */
  getStatus: async (entityName) => {
    const response = await apiClient.get('/api/v1/socie/status', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Generate SOCIE report
   */
  generateSOCIE: async (
    entityName,
    currentStartDate,
    currentEndDate,
    previousStartDate,
    previousEndDate,
    movementsTimestamp = null
  ) => {
    const payload = {
      entity_name: entityName,
      current_start_date: currentStartDate,
      current_end_date: currentEndDate,
      previous_start_date: previousStartDate,
      previous_end_date: previousEndDate,
    };
    if (movementsTimestamp) {
      payload.movements_timestamp = movementsTimestamp;
    }
    const response = await apiClient.post('/api/v1/socie/generate', payload);
    return response.data;
  },

  /**
   * List all SOCIE outputs
   */
  listOutputs: async (entityName) => {
    const response = await apiClient.get('/api/v1/socie/outputs', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download SOCIE output
   */
  downloadOutput: async (entityName, period, timestamp = null) => {
    const params = {
      entity_name: entityName,
      period: period,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/socie/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// ESOP API
export const esopApi = {
  /**
   * Download ESOP template
   * @returns {Promise<Blob>}
   */
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/socie/esop/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload ESOP data
   * @param {string} entityName - Entity name
   * @param {File} file - Excel file with ESOP data
   * @returns {Promise<Object>}
   */
  uploadData: async (entityName, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_name', entityName);
    formData.append('environment', 'dev');

    const response = await apiClient.post('/api/v1/socie/esop/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Get ESOP input status
   * @param {string} entityName - Entity name
   * @returns {Promise<Object>}
   */
  getStatus: async (entityName) => {
    const response = await apiClient.get('/api/v1/socie/esop/status', {
      params: {
        entity_name: entityName,
        environment: 'dev',
      },
    });
    return response.data;
  },

  /**
   * Generate ESOP workings
   * @param {string} entityName - Entity name
   * @param {Array<Object>} fiscalYears - Fiscal year periods
   * @returns {Promise<Object>}
   */
  generate: async (entityName, fiscalYears) => {
    const response = await apiClient.post('/api/v1/socie/esop/generate', {
      entity_name: entityName,
      fiscal_years: fiscalYears,
      environment: 'dev',
    });
    return response.data;
  },

  /**
   * List ESOP outputs
   * @param {string} entityName - Entity name
   * @returns {Promise<Object>}
   */
  listOutputs: async (entityName) => {
    const response = await apiClient.get('/api/v1/socie/esop/outputs', {
      params: {
        entity_name: entityName,
        environment: 'dev',
      },
    });
    return response.data;
  },

  /**
   * Download ESOP output
   * @param {string} entityName - Entity name
   * @param {string} period - Period identifier
   * @param {string} [timestamp] - Optional timestamp
   * @returns {Promise<Blob>}
   */
  downloadOutput: async (entityName, period, timestamp = null) => {
    const params = {
      entity_name: entityName,
      period,
      environment: 'dev',
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }
    const response = await apiClient.get('/api/v1/socie/esop/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// Common Template Operations
export const templateApi = {
  /**
   * Get list of available template types
   * @returns {Promise<Object>}
   */
  getTemplateTypes: async () => {
    const response = await apiClient.get('/api/v1/common/template/types');
    return response.data;
  },

  /**
   * Validate uploaded template file
   * @param {File} file - Excel file to validate
   * @param {string} templateType - Template type (borrowing, esop, far, fd, gl, lease, socie, tb)
   * @returns {Promise<Object>}
   */
  verifyTemplate: async (file, templateType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_type', templateType);

    const response = await apiClient.post('/api/v1/common/template/verify', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Generate filled template from source data
   * @param {File} file - Source data Excel file
   * @param {string} templateType - Template type (borrowing, esop, far, fd, gl, lease, socie, tb)
   * @returns {Promise<Blob>}
   */
  generateTemplate: async (file, templateType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_type', templateType);

    try {
      const response = await apiClient.post('/api/v1/common/template/generate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      // If error response is a blob (due to responseType: 'blob'), convert it to JSON
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const jsonError = JSON.parse(text);
          // Re-throw with proper structure
          throw {
            isApiError: true,
            data: jsonError,
            status: error.response.status,
            message: jsonError.message || 'Generation failed'
          };
        } catch (parseError) {
          // If not JSON, just throw original error
          throw error;
        }
      }
      throw error;
    }
  },

  /**
   * Health check for template service
   * @returns {Promise<Object>}
   */
  healthCheck: async () => {
    const response = await apiClient.get('/api/v1/common/health');
    return response.data;
  },
};

// AR Ageing Operations
export const arAgingApi = {
  /**
   * Generate AR ageing report for an entity
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date (end date) in YYYY-MM-DD format
   * @param {string} startDate - Optional start date for date range filtering (YYYY-MM-DD format)
   * @param {File|null} localDumpFile - Optional INR local-currency dump for
   *   Advance-customer rows. When provided, matched rows use the dump's INR
   *   Balance Due and the Booking Rate becomes an Excel formula INR/FC.
   * @returns {Promise<Object>} - AR ageing report result
   */
  generateReport: async (entityName, reportingDate, startDate = null, localDumpFile = null, enableZeroBalanceExclusion = false, bucketAdjustments = null) => {
    const hasAdjustments = bucketAdjustments && Object.keys(bucketAdjustments).length > 0;
    // Multipart path when a local dump file is supplied
    if (localDumpFile) {
      const formData = new FormData();
      formData.append('entity_name', entityName);
      formData.append('reporting_date', reportingDate);
      if (startDate) {
        formData.append('start_date', startDate);
      }
      formData.append('local_dump_file', localDumpFile);
      formData.append('enable_zero_balance_exclusion', enableZeroBalanceExclusion ? 'true' : 'false');
      if (hasAdjustments) {
        formData.append('bucket_adjustments', JSON.stringify(bucketAdjustments));
      }
      const response = await apiClient.post('/api/v1/ar-aging/generate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }

    // Existing JSON path (no behavioural change when no file)
    const payload = {
      entity_name: entityName,
      reporting_date: reportingDate,
      enable_zero_balance_exclusion: enableZeroBalanceExclusion,
    };
    if (startDate) {
      payload.start_date = startDate;
    }
    if (hasAdjustments) {
      payload.bucket_adjustments = bucketAdjustments;
    }
    const response = await apiClient.post('/api/v1/ar-aging/generate', payload);
    return response.data;
  },

  /**
   * List available AR ageing reports for an entity
   * @param {string} entityName - Entity name
   * @returns {Promise<Object>} - List of reports
   */
  listReports: async (entityName) => {
    const response = await apiClient.get('/api/v1/ar-aging/list', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download AR ageing report
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @param {string} timestamp - Optional timestamp (defaults to latest)
   * @returns {Promise<Blob>} - Excel file blob
   */
  downloadReport: async (entityName, reportingDate, timestamp = null) => {
    const params = {
      entity_name: entityName,
      reporting_date: reportingDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }

    const response = await apiClient.get('/api/v1/ar-aging/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get AR ageing status for one or all entities
   * @param {string} entityName - Optional entity name (returns all if not specified)
   * @returns {Promise<Object>} - Status information
   */
  getStatus: async (entityName = null) => {
    const params = entityName ? { entity_name: entityName } : {};
    const response = await apiClient.get('/api/v1/ar-aging/status', { params });
    return response.data;
  },

  /**
   * Download AR ageing template for manual upload
   * @returns {Promise<Blob>} - Excel template file blob
   */
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/ar-aging/download-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload AR ageing template - Simple template processing
   * For SAP entities (alternative to SAP query) and manual entities
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @param {File} file - Excel file with AR template
   * @param {string|null} baseCurrency - Base currency for manual entities (optional)
   * @param {File|null} fxRatesFile - FX rates file for manual entities (optional)
   * @param {File|null} localDumpFile - Optional INR local-currency dump for
   *   Advance-customer rows (TL SAP entities). Matched Invoice No rows use
   *   the dump's INR Balance Due and the Booking Rate becomes an Excel formula.
   * @returns {Promise<Object>} - Upload result
   */
  uploadTemplateSimple: async (entityName, reportingDate, file, baseCurrency = null, fxRatesFile = null, localDumpFile = null, startDate = null, enableZeroBalanceExclusion = false, bucketAdjustments = null) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('reporting_date', reportingDate);
    formData.append('file', file);
    if (baseCurrency) {
      formData.append('base_currency', baseCurrency);
    }
    if (fxRatesFile) {
      formData.append('fx_rates_file', fxRatesFile);
    }
    if (localDumpFile) {
      formData.append('local_dump_file', localDumpFile);
    }
    formData.append('enable_zero_balance_exclusion', enableZeroBalanceExclusion ? 'true' : 'false');
    if (bucketAdjustments && Object.keys(bucketAdjustments).length > 0) {
      formData.append('bucket_adjustments', JSON.stringify(bucketAdjustments));
    }
    // start_date is required for entities that fetch TB from S3 (e.g. everlife_holding_pte_ltd).
    // The backend uses it to build the S3 folder <start_date>_to_<reporting_date>/.
    if (startDate) {
      formData.append('start_date', startDate);
    }

    const response = await apiClient.post('/api/v1/ar-aging/upload-template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download FX rates template for manual entities
   * @returns {Promise<Blob>} - Excel template file blob
   */
  downloadFxTemplate: async () => {
    const response = await apiClient.get('/api/v1/ar-aging/download-fx-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download AR Finalyzer push template
   * @returns {Promise<Blob>} - Excel template file blob
   */
  downloadFinalyzerTemplate: async () => {
    const response = await apiClient.get('/api/v1/ar-aging/download-finalyzer-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Push AR schedule data to Finalyzer
   * @param {string} entityName - Entity name
   * @param {string} toDate - To date in YYYY-MM-DD format
   * @param {File} file - Excel file with AR Finalyzer data
   * @returns {Promise<Object>} - Push result
   */
  pushToFinalyzer: async (entityName, toDate, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('to_date', toDate);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/ar-aging/push-to-finalyzer', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download the auto-generated AR Finalyzer push-template from S3
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Blob>} - Excel file blob
   */
  downloadFinalyzerOutput: async (entityName, reportingDate) => {
    const response = await apiClient.get('/api/v1/ar-aging/download-finalyzer-output', {
      params: { entity_name: entityName, reporting_date: reportingDate },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Push AR Receivables to Finalyzer using the auto-generated template from S3.
   * No file upload required.
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Object>} - Push result
   */
  pushToFinalyzerFromS3: async (entityName, reportingDate) => {
    const response = await apiClient.post('/api/v1/ar-aging/push-to-finalyzer-from-s3', {
      entity_name: entityName,
      reporting_date: reportingDate,
    });
    return response.data;
  },

  /**
   * Generate consolidated AR ageing report for all TL_RPT entities
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Blob>} - Consolidated Excel file blob
   */
  generateConsolidated: async (reportingDate) => {
    const response = await apiClient.post('/api/v1/ar-aging/consolidate', {
      reporting_date: reportingDate,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Generate the EL-group consolidated AR ageing console (all 20 EL entities)
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Blob>} - Consolidated Excel file blob
   */
  generateELConsolidated: async (reportingDate) => {
    const response = await apiClient.post('/api/v1/ar-aging/consolidate-el', {
      reporting_date: reportingDate,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download the latest previously-generated EL-group consolidated AR console
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Blob>} - Consolidated Excel file blob
   */
  downloadELConsolidated: async (reportingDate) => {
    const response = await apiClient.post('/api/v1/ar-aging/consolidate-el/download', {
      reporting_date: reportingDate,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * List reporting dates for which an EL AR console has already been generated
   * @returns {Promise<Array<{reporting_date: string, last_modified: string, count: number}>>}
   */
  listELConsolidated: async () => {
    const response = await apiClient.get('/api/v1/ar-aging/consolidate-el/list');
    return (response.data && response.data.periods) || [];
  },

  /**
   * Check whether an EL AR console has already been generated for a specific date
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<{available: boolean, generated_date?: string, reason?: string}|null>}
   */
  statusELConsolidated: async (reportingDate) => {
    const response = await apiClient.post('/api/v1/ar-aging/consolidate-el/status', {
      reporting_date: reportingDate,
    });
    return (response.data && response.data.status) || null;
  },
};

// AP Ageing Operations
export const apAgingApi = {
  /**
   * Generate AP ageing report for an entity
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date (end date) in YYYY-MM-DD format
   * @param {string} startDate - Optional start date for date range filtering (YYYY-MM-DD format)
   * @param {File|null} localDumpFile - Optional INR local-currency dump for
   *   foreign-currency Advance-vendor rows. When provided, the matched rows
   *   in the AP detail use the dump's INR Balance Due (and Invoice/Paid if
   *   present) instead of (FC × Booking Rate).
   * @returns {Promise<Object>} - AP ageing report result
   */
  generateReport: async (entityName, reportingDate, startDate = null, localDumpFile = null, enableZeroBalanceExclusion = false, bucketAdjustments = null) => {
    const hasAdjustments = bucketAdjustments && Object.keys(bucketAdjustments).length > 0;
    // Multipart path when a local dump file is supplied
    if (localDumpFile) {
      const formData = new FormData();
      formData.append('entity_name', entityName);
      formData.append('reporting_date', reportingDate);
      if (startDate) {
        formData.append('start_date', startDate);
      }
      formData.append('local_dump_file', localDumpFile);
      formData.append('enable_zero_balance_exclusion', enableZeroBalanceExclusion ? 'true' : 'false');
      if (hasAdjustments) {
        formData.append('bucket_adjustments', JSON.stringify(bucketAdjustments));
      }
      const response = await apiClient.post('/api/v1/ap-aging/generate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }

    // Existing JSON path (no behavioural change when no file)
    const payload = {
      entity_name: entityName,
      reporting_date: reportingDate,
      enable_zero_balance_exclusion: enableZeroBalanceExclusion,
    };
    if (startDate) {
      payload.start_date = startDate;
    }
    if (hasAdjustments) {
      payload.bucket_adjustments = bucketAdjustments;
    }
    const response = await apiClient.post('/api/v1/ap-aging/generate', payload);
    return response.data;
  },

  /**
   * List available AP ageing reports for an entity
   * @param {string} entityName - Entity name
   * @returns {Promise<Object>} - List of reports
   */
  listReports: async (entityName) => {
    const response = await apiClient.get('/api/v1/ap-aging/list', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download AP ageing report
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @param {string} timestamp - Optional timestamp (defaults to latest)
   * @returns {Promise<Blob>} - Excel file blob
   */
  downloadReport: async (entityName, reportingDate, timestamp = null) => {
    const params = {
      entity_name: entityName,
      reporting_date: reportingDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }

    const response = await apiClient.get('/api/v1/ap-aging/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get AP ageing status for one or all entities
   * @param {string} entityName - Optional entity name (returns all if not specified)
   * @returns {Promise<Object>} - Status information
   */
  getStatus: async (entityName = null) => {
    const params = entityName ? { entity_name: entityName } : {};
    const response = await apiClient.get('/api/v1/ap-aging/status', { params });
    return response.data;
  },

  /**
   * Download AP ageing template for manual upload
   * @returns {Promise<Blob>} - Excel template file blob
   */
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/ap-aging/download-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload AP ageing template - Simple template processing
   * For SAP entities (alternative to SAP query) and manual entities
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @param {File} file - Excel file with AP template
   * @param {string|null} baseCurrency - Base currency for manual entities (optional)
   * @param {File|null} fxRatesFile - FX rates file for manual entities (optional)
   * @param {File|null} localDumpFile - Optional INR local-currency dump for
   *   foreign-currency Advance-vendor rows (TL SAP entities). Matched
   *   Invoice No rows use the dump's INR amounts instead of (FC × Booking Rate).
   * @returns {Promise<Object>} - Upload result
   */
  uploadTemplateSimple: async (entityName, reportingDate, file, baseCurrency = null, fxRatesFile = null, localDumpFile = null, startDate = null, enableZeroBalanceExclusion = false, bucketAdjustments = null) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('reporting_date', reportingDate);
    formData.append('file', file);
    if (baseCurrency) {
      formData.append('base_currency', baseCurrency);
    }
    if (fxRatesFile) {
      formData.append('fx_rates_file', fxRatesFile);
    }
    if (localDumpFile) {
      formData.append('local_dump_file', localDumpFile);
    }
    formData.append('enable_zero_balance_exclusion', enableZeroBalanceExclusion ? 'true' : 'false');
    if (bucketAdjustments && Object.keys(bucketAdjustments).length > 0) {
      formData.append('bucket_adjustments', JSON.stringify(bucketAdjustments));
    }
    // start_date is required for entities that fetch TB from S3 (e.g. everlife_holding_pte_ltd).
    // The backend uses it to build the S3 folder <start_date>_to_<reporting_date>/.
    if (startDate) {
      formData.append('start_date', startDate);
    }

    const response = await apiClient.post('/api/v1/ap-aging/upload-template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download FX rates template for manual entities
   * @returns {Promise<Blob>} - Excel template file blob
   */
  downloadFxTemplate: async () => {
    const response = await apiClient.get('/api/v1/ap-aging/download-fx-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download AP Finalyzer push template
   * @returns {Promise<Blob>} - Excel template file blob
   */
  downloadFinalyzerTemplate: async () => {
    const response = await apiClient.get('/api/v1/ap-aging/download-finalyzer-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Push AP schedule data to Finalyzer
   * @param {string} entityName - Entity name
   * @param {string} toDate - To date in YYYY-MM-DD format
   * @param {File} file - Excel file with AP Finalyzer data
   * @returns {Promise<Object>} - Push result
   */
  pushToFinalyzer: async (entityName, toDate, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('to_date', toDate);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/ap-aging/push-to-finalyzer', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download the auto-generated AP Finalyzer push-template from S3
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Blob>} - Excel file blob
   */
  downloadFinalyzerOutput: async (entityName, reportingDate) => {
    const response = await apiClient.get('/api/v1/ap-aging/download-finalyzer-output', {
      params: { entity_name: entityName, reporting_date: reportingDate },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Push AP Payables to Finalyzer using the auto-generated template from S3.
   * No file upload required.
   * @param {string} entityName - Entity name
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Object>} - Push result
   */
  pushToFinalyzerFromS3: async (entityName, reportingDate) => {
    const response = await apiClient.post('/api/v1/ap-aging/push-to-finalyzer-from-s3', {
      entity_name: entityName,
      reporting_date: reportingDate,
    });
    return response.data;
  },

  /**
   * Generate consolidated AP ageing report for all TL_RPT entities
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Blob>} - Consolidated Excel file blob
   */
  generateConsolidated: async (reportingDate) => {
    const response = await apiClient.post('/api/v1/ap-aging/consolidate', {
      reporting_date: reportingDate,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Generate the EL-group consolidated AP ageing console (all 20 EL entities)
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Blob>} - Consolidated Excel file blob
   */
  generateELConsolidated: async (reportingDate) => {
    const response = await apiClient.post('/api/v1/ap-aging/consolidate-el', {
      reporting_date: reportingDate,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download the latest previously-generated EL-group consolidated AP console
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<Blob>} - Consolidated Excel file blob
   */
  downloadELConsolidated: async (reportingDate) => {
    const response = await apiClient.post('/api/v1/ap-aging/consolidate-el/download', {
      reporting_date: reportingDate,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * List reporting dates for which an EL AP console has already been generated
   * @returns {Promise<Array<{reporting_date: string, last_modified: string, count: number}>>}
   */
  listELConsolidated: async () => {
    const response = await apiClient.get('/api/v1/ap-aging/consolidate-el/list');
    return (response.data && response.data.periods) || [];
  },

  /**
   * Check whether an EL AP console has already been generated for a specific date
   * @param {string} reportingDate - Reporting date in YYYY-MM-DD format
   * @returns {Promise<{available: boolean, generated_date?: string, reason?: string}|null>}
   */
  statusELConsolidated: async (reportingDate) => {
    const response = await apiClient.post('/api/v1/ap-aging/consolidate-el/status', {
      reporting_date: reportingDate,
    });
    return (response.data && response.data.status) || null;
  },
};

/**
 * Journal Adjustments API
 */
export const jeAdjustmentsApi = {
  /**
   * Upload journal adjustments from Excel file
   * @param {string} entityName - Entity name
   * @param {string} adjustmentDate - Adjustment date in YYYY-MM-DD format
   * @param {File} file - Excel file with journal entries
   * @returns {Promise<Object>} - Upload result
   */
  uploadJournalEntries: async (entityName, adjustmentDate, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('adjustment_date', adjustmentDate);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/je-adjustments/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * List available journal adjustments for an entity
   * @param {string} entityName - Entity name
   * @returns {Promise<Object>} - List of journal adjustments
   */
  listReports: async (entityName) => {
    const response = await apiClient.get('/api/v1/je-adjustments/list', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  /**
   * Download journal adjustments Excel file
   * @param {string} entityName - Entity name
   * @param {string} adjustmentDate - Adjustment date in YYYY-MM-DD format
   * @param {string} timestamp - Optional timestamp (defaults to latest)
   * @returns {Promise<Blob>} - Excel file blob
   */
  downloadReport: async (entityName, adjustmentDate, timestamp = null) => {
    const params = {
      entity_name: entityName,
      adjustment_date: adjustmentDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }

    const response = await apiClient.get('/api/v1/je-adjustments/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download the journal entry template
   * @returns {Promise<Blob>} - Excel template file blob
   */
  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/je-adjustments/download-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Push journal entries to Finalyzer (V1 - postJournalEntry endpoint)
   * @param {string} entityName - Entity name
   * @param {string} adjustmentDate - Adjustment date in YYYY-MM-DD format
   * @param {string} timestamp - Optional timestamp (defaults to latest)
   * @returns {Promise<Object>} - Push result
   */
  pushToFinalyzer: async (entityName, adjustmentDate, timestamp = null) => {
    const payload = {
      entity_name: entityName,
      adjustment_date: adjustmentDate,
    };
    if (timestamp) {
      payload.timestamp = timestamp;
    }

    const response = await apiClient.post('/api/v1/je-adjustments/push-to-finalyzer', payload);
    return response.data;
  },

  /**
   * Push journal entries to Finalyzer V2 (postTrialBalance endpoint)
   * Uses hardcoded taxonomy_name and data_mnemonic for adjustment entries.
   * @param {string} entityName - Entity name
   * @param {string} adjustmentDate - Adjustment date in YYYY-MM-DD format
   * @param {string} timestamp - Optional timestamp (defaults to latest)
   * @returns {Promise<Object>} - Push result
   */
  pushToFinalyzerV2: async (entityName, adjustmentDate, timestamp = null) => {
    const payload = {
      entity_name: entityName,
      adjustment_date: adjustmentDate,
    };
    if (timestamp) {
      payload.timestamp = timestamp;
    }

    const response = await apiClient.post('/api/v1/je-adjustments/push-to-finalyzer-v2', payload);
    return response.data;
  },

  /**
   * Get journal adjustments status for one or all entities
   * @param {string} entityName - Optional entity name (returns all if not specified)
   * @returns {Promise<Object>} - Status information
   */
  getStatus: async (entityName = null) => {
    const params = entityName ? { entity_name: entityName } : {};
    const response = await apiClient.get('/api/v1/je-adjustments/status', { params });
    return response.data;
  },
};

/**
 * Consol Adjustments API (Consolidated adjustments - same sheet format, push with nature_of_report: "Consolidated")
 */
export const consolAdjustmentsApi = {
  uploadJournalEntries: async (entityName, adjustmentDate, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('adjustment_date', adjustmentDate);
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/consol-adjustments/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  listReports: async (entityName) => {
    const response = await apiClient.get('/api/v1/consol-adjustments/list', {
      params: { entity_name: entityName },
    });
    return response.data;
  },

  downloadReport: async (entityName, adjustmentDate, timestamp = null) => {
    const params = {
      entity_name: entityName,
      adjustment_date: adjustmentDate,
    };
    if (timestamp) {
      params.timestamp = timestamp;
    }

    const response = await apiClient.get('/api/v1/consol-adjustments/download', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  downloadTemplate: async () => {
    const response = await apiClient.get('/api/v1/consol-adjustments/download-template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Push consol adjustments to Finalyzer V2 (adds nature_of_report: "Consolidated")
   */
  pushToFinalyzerV2: async (entityName, adjustmentDate, timestamp = null) => {
    const payload = {
      entity_name: entityName,
      adjustment_date: adjustmentDate,
    };
    if (timestamp) {
      payload.timestamp = timestamp;
    }

    const response = await apiClient.post('/api/v1/consol-adjustments/push-to-finalyzer-v2', payload);
    return response.data;
  },

  getStatus: async (entityName = null) => {
    const params = entityName ? { entity_name: entityName } : {};
    const response = await apiClient.get('/api/v1/consol-adjustments/status', { params });
    return response.data;
  },
};

// Cashflow Operations
export const cashflowApi = {
  /**
   * Generate cashflow statement with all workings
   * @param {string} entityName - Entity name
   * @param {string} startDate - Period start date in YYYY-MM-DD format
   * @param {string} endDate - Period end date in YYYY-MM-DD format
   * @returns {Promise<Object>} - Generation result with S3 path and download URL
   */
  generateCashflow: async (entityName, startDate, endDate) => {
    const response = await apiClient.post('/api/v1/cashflow/generate', {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
      async_mode: false,
    });
    return response.data;
  },

  /**
   * Queue async cashflow generation and return job id
   */
  generateCashflowAsync: async (entityName, startDate, endDate) => {
    const response = await apiClient.post('/api/v1/cashflow/generate', {
      entity_name: entityName,
      start_date: startDate,
      end_date: endDate,
      async_mode: true,
    });
    return response.data;
  },

  /**
   * Get async cashflow generation status
   */
  getCashflowGenerateStatus: async (jobId) => {
    const response = await apiClient.get(`/api/v1/cashflow/generate/status/${jobId}`);
    return response.data;
  },

  /**
   * List all cashflow outputs for an entity
   * @param {string} entityName - Entity name
   * @returns {Promise<Object>} - List of cashflow outputs grouped by period
   */
  listCashflows: async (entityName) => {
    const response = await apiClient.get(`/api/v1/cashflow/list/${entityName}`);
    return response.data;
  },

  /**
   * Download cashflow Excel output
   * @param {string} entityName - Entity name
   * @param {string} periodLabel - Period label (e.g., "2025-04-01_to_2025-12-31")
   * @returns {Promise<Blob>} - Excel file blob
   */
  downloadCashflow: async (entityName, periodLabel) => {
    const response = await apiClient.get(`/api/v1/cashflow/download/${entityName}/${periodLabel}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Send cashflow data to Finalyzer
   * @param {string} entityName - Entity name
   * @param {string} periodLabel - Period label (e.g., "2025-04-01_to_2025-12-31")
   * @param {string} toDate - Finalyzer to_date in DD-MMM-YYYY format (e.g., "31-Dec-2025")
   * @param {string} timestamp - Optional timestamp (defaults to latest)
   * @returns {Promise<Object>} - Finalyzer submission result
   */
  sendToFinalyzer: async (entityName, periodLabel, toDate, timestamp = null) => {
    const payload = {
      entity_name: entityName,
      period_label: periodLabel,
      to_date: toDate,
    };
    if (timestamp) {
      payload.timestamp = timestamp;
    }

    const response = await apiClient.post('/api/v1/cashflow/send-finalyzer', payload);
    return response.data;
  },
};

// Consolidated RPT API (Intercompany)
export const consolidatedRptApi = {
  /**
   * Generate consolidated RPT report for all EL_SAP_B1 entities
   * @param {Object} options - Generation options
   * @param {string} options.start_date - Start date (YYYY-MM-DD)
   * @param {string} options.end_date - End date (YYYY-MM-DD)
   * @param {boolean} options.use_existing - Use existing RPT if available (default: true)
   * @param {boolean} options.use_llm_classification - Use LLM for classification (default: false)
   * @returns {Promise<Object>} - Generation result with S3 path and summary
   */
  generate: async (options) => {
    const response = await apiClient.post('/api/v1/rpt-v2/consolidate', options, {
      timeout: 600000, // 10 minutes timeout for this long operation
    });
    return response.data;
  },

  /**
   * List all consolidated RPT reports
   * @param {string} period - Optional period filter
   * @returns {Promise<Object>} - List of reports with metadata
   */
  listReports: async (period = null) => {
    const params = period ? { period } : {};
    const response = await apiClient.get('/api/v1/rpt-v2/consolidate/list', { params });
    return response.data;
  },

  /**
   * Download consolidated RPT Excel file
   * @param {string} period - Period string (e.g., "2025-04-01_to_2025-12-31")
   * @param {string} timestamp - Generation timestamp
   * @returns {Promise<Blob>} - Excel file blob
   */
  download: async (period, timestamp) => {
    const response = await apiClient.get('/api/v1/rpt-v2/consolidate/download', {
      params: { period, timestamp },
      responseType: 'blob',
    });
    return response.data;
  },
};

// TL Group RPT API
export const tlRptApi = {
  status: async (startDate, endDate) => {
    const response = await apiClient.get('/api/v1/tl-rpt/status', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  fetchSapGL: async (entityCode, startDate, endDate) => {
    const response = await apiClient.post('/api/v1/tl-rpt/fetch-sap-gl', {
      entity_code: entityCode, start_date: startDate, end_date: endDate,
    });
    return response.data;
  },

  fetchBmd: async (startDate, endDate) => {
    const response = await apiClient.post('/api/v1/tl-rpt/fetch-bmd', {
      start_date: startDate, end_date: endDate,
    });
    return response.data;
  },

 
  generate: async (startDate, endDate, entities = null) => {
    const body = { start_date: startDate, end_date: endDate };
    if (entities && entities.length > 0) body.entities = entities;
    const response = await apiClient.post('/api/v1/tl-rpt/generate', body, { timeout: 600000 });
    return response.data;
  },

  downloadEntityGL: async (entityCode, startDate, endDate) => {
    const response = await apiClient.get('/api/v1/tl-rpt/download-entity-gl', {
      params: { entity_code: entityCode, start_date: startDate, end_date: endDate },
      responseType: 'blob',
    });
    return response.data;
  },

  downloadManualGL: async (entityName, startDate, endDate) => {
    const response = await apiClient.get('/api/v1/tl-rpt/download-manual-gl', {
      params: { entity_name: entityName, start_date: startDate, end_date: endDate },
      responseType: 'blob',
    });
    return response.data;
  },

  download: async (startDate, endDate) => {
    const response = await apiClient.get('/api/v1/tl-rpt/download', {
      params: { start_date: startDate, end_date: endDate },
      responseType: 'blob',
    });
    return response.data;
  },

  glTemplate: async (entityName, glType = 'general_ledger') => {
    const response = await apiClient.get('/api/v1/tl-rpt/gl-template', {
      params: { entity_name: entityName, gl_type: glType },
      responseType: 'blob',
    });
    return response.data;
  },

  fetchGLExtract: async (entityCode, startDate, endDate) => {
    const response = await apiClient.post('/api/v1/tl-rpt/fetch-gl-extract', {
      entity_code: entityCode, start_date: startDate, end_date: endDate,
    }, { timeout: 300000 });
    return response.data;
  },

  downloadGLExtract: async (entityCode, startDate, endDate) => {
    const response = await apiClient.get('/api/v1/tl-rpt/download-gl-extract', {
      params: { entity_code: entityCode, start_date: startDate, end_date: endDate },
      responseType: 'blob',
    });
    return response.data;
  },
};

// ROU FinAlyzer schedule push API
export const rouFinAlyzerApi = {
  // List entities configured in configure_rou_finalyzer.yaml
  listEntities: async () => {
    const response = await apiClient.get('/api/v1/rou-finalyzer/entities');
    return response.data;
  },

  // Generate a blank/pre-filled template and store it on S3
  generateTemplate: async (entityName, periodEndDate, amounts = null) => {
    const payload = { entity_name: entityName, period_end_date: periodEndDate };
    if (amounts) payload.amounts = amounts;
    const response = await apiClient.post('/api/v1/rou-finalyzer/generate-template', payload);
    return response.data;
  },

  // Download the latest template for an entity + period (returns Excel blob)
  downloadTemplate: async (entityName, periodEndDate) => {
    const response = await apiClient.get('/api/v1/rou-finalyzer/download-template', {
      params: { entity_name: entityName, period_end_date: periodEndDate },
      responseType: 'blob',
    });
    return response.data;
  },

  // Upload a user-modified template back to S3
  uploadTemplate: async (entityName, periodEndDate, file) => {
    const formData = new FormData();
    formData.append('entity_name', entityName);
    formData.append('period_end_date', periodEndDate);
    formData.append('file', file);
    const response = await apiClient.post('/api/v1/rou-finalyzer/upload-template', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Push latest S3 template data to FinAlyzer
  push: async (entityName, periodEndDate) => {
    const response = await apiClient.post('/api/v1/rou-finalyzer/push', {
      entity_name: entityName,
      period_end_date: periodEndDate,
    });
    return response.data;
  },

  // List templates stored on S3 for an entity
  listTemplates: async (entityName, periodEndDate = null) => {
    const params = { entity_name: entityName };
    if (periodEndDate) params.period_end_date = periodEndDate;
    const response = await apiClient.get('/api/v1/rou-finalyzer/list-templates', { params });
    return response.data;
  },
};

// Health check
export const healthCheck = async () => {
  const response = await apiClient.get('/health');
  return response.data;
};

export default apiClient;
